import { defineComponent, ref, computed } from 'vue'
import { useLottoStats } from '@/composables/use-lotto-stats'
import type { NumberStatItem, StringStatItem } from '@/composables/use-lotto-stats'
import type { BallColor } from '@/components/lottery-ball-row'

interface ConfirmData {
  title: string
  item: string
  num: number
}

const COLOR_KEY: Record<string, 'red' | 'green' | 'blue'> = {
  红波: 'red',
  绿波: 'green',
  蓝波: 'blue',
}

function ballColorClass(c: BallColor) {
  if (c === 'green') return 'green'
  if (c === 'blue') return 'blue'
  return 'red'
}

interface NumberRow {
  name: string
  items: NumberStatItem[]
  label: string
}

interface TextRow {
  name: string
  items: StringStatItem[]
}

export default defineComponent({
  name: 'LottoStatsPage',
  setup() {
    const stats = useLottoStats()

    const confirmShow = ref(false)
    const confirmData = ref<ConfirmData>({ title: '', item: '', num: 0 })

    const showNumber = (item: NumberStatItem, title: string, label: string) => {
      confirmData.value = {
        title,
        item: `${label} ${String(item.number).padStart(2, '0')}`,
        num: item.count,
      }
      confirmShow.value = true
    }

    const showText = (item: StringStatItem, title: string) => {
      confirmData.value = { title, item: item.label, num: item.count }
      confirmShow.value = true
    }

    // 卡 1:号码(.top-wrap × 4)
    const numberRows = computed(() => [
      { name: '特码出现期数最多的号码', items: stats.specialNumFreq.value, label: '出现' },
      { name: '特码当前遗漏期数最多的号码', items: stats.specialNumMissing.value, label: '遗漏' },
      { name: '正码出现期数最多的号码', items: stats.regularNumFreq.value, label: '出现' },
      { name: '正码当前遗漏期数最多的号码', items: stats.regularNumMissing.value, label: '遗漏' },
    ])

    // 卡 2:生肖(.bottom-wrap × 4)
    const zodiacRows = computed(() => [
      { name: '特码出现期数最多的生肖', items: stats.specialZodiacFreq.value },
      { name: '特码当前遗漏期数最多的生肖', items: stats.specialZodiacMissing.value },
      { name: '正码出现期数最多的生肖', items: stats.regularZodiacFreq.value },
      { name: '正码当前遗漏期数最多的生肖', items: stats.regularZodiacMissing.value },
    ])

    // 卡 3:波色(.bottom-wrap × 4 + .red-text)
    const colorRows = computed(() => [
      { name: '特码出现期数最多的波色', items: stats.specialColorFreq.value },
      { name: '特码当前遗漏期数最多的波色', items: stats.specialColorMissing.value },
      { name: '正码出现期数最多的波色', items: stats.regularColorFreq.value },
      { name: '正码当前遗漏期数最多的波色', items: stats.regularColorMissing.value },
    ])

    // 卡 4:尾数(.bottom-wrap × 2)
    const tailRows = computed(() => [
      { name: '特码出现期数最多的尾数', items: stats.specialTailFreq.value },
      { name: '特码当前遗漏期数最多的尾数', items: stats.specialTailMissing.value },
    ])

    /** 卡 1 的一行：号码球 */
    const renderNumberRow = (row: NumberRow) => (
      <div class="top-wrap" key={row.name}>
        <h4>{row.name}</h4>
        <div class="color-wrap">
          {row.items.map((item) => (
            // 样式挂在 `.color-wrap > div` 上：div 保留，按钮放里面
            <div key={item.number} class={ballColorClass(item.color)}>
              <button type="button" class="btn-reset w-full" onClick={() => showNumber(item, row.name, row.label)}>
                {String(item.number).padStart(2, '0')}
              </button>
            </div>
          ))}
        </div>
      </div>
    )

    /** 卡 2 / 卡 4 的一行：生肖、尾数 */
    const renderTextRow = (row: TextRow) => (
      <div class="bottom-wrap" key={row.name}>
        <div>{row.name}</div>
        <div>
          {row.items.map((item) => (
            // 样式挂在 `.bottom-wrap > div:nth-of-type(2) > div` 上：div 保留，按钮放里面
            <div key={item.label}>
              <button type="button" class="btn-reset" onClick={() => showText(item, row.name)}>
                {item.label}
              </button>
            </div>
          ))}
        </div>
      </div>
    )

    /** 卡 3 的一行：波色 */
    const renderColorRow = (row: TextRow) => (
      <div class="bottom-wrap" key={row.name}>
        <div>{row.name}</div>
        <div class="red-text">
          {row.items.map((item) => (
            <div key={item.label} class={COLOR_KEY[item.label] ?? 'red'}>
              <button type="button" class="btn-reset" onClick={() => showText(item, row.name)}>
                {item.label}
              </button>
            </div>
          ))}
        </div>
      </div>
    )

    return () => (
      <div class="mint-tab-container-wrap">
        {stats.loading.value ? null : (
          <div class="mint-tab-container-item">
            {/* 卡 1:号码 */}
            <div class="container-wrap">
              {numberRows.value.map(renderNumberRow)}
            </div>

            {/* 卡 2:生肖 */}
            <div class="container-wrap">
              {zodiacRows.value.map(renderTextRow)}
            </div>

            {/* 卡 3:波色 */}
            <div class="container-wrap">
              {colorRows.value.map(renderColorRow)}
            </div>

            {/* 卡 4:尾数(底部留 80px 给浮动按钮) */}
            <div class="container-wrap" style={{ paddingBottom: '80px' }}>
              {tailRows.value.map(renderTextRow)}
            </div>
          </div>
        )}

        {confirmShow.value && (
          <div class="confirm">
            <div class="confirm-lhbody">
              <h4>{confirmData.value.title}</h4>
              <p>
                【{confirmData.value.item}】统计的次数:{confirmData.value.num}次
              </p>
              <button type="button" class="comfirm-btn btn-block" onClick={() => (confirmShow.value = false)}>
                确定
              </button>
            </div>
          </div>
        )}
      </div>
    )
  },
})
